import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import StudentLayout from "@/components/StudentLayout";
import api from "@/utils/api";
import DOMPurify from "dompurify";
import { BookOpen, CheckCircle, Clock, Eye, FileText, Download, ExternalLink, StickyNote, Image as ImageIcon } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function GuardianDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/guardian/dashboard").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-[#5C3A2E]/50 font-body">Loading...</div>
        </div>
      </StudentLayout>
    );
  }

  const students = data?.students || [];

  return (
    <StudentLayout>
      <div data-testid="guardian-dashboard" className="space-y-10 animate-fade-in-up">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#5C3A2E] font-semibold tracking-tight">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-[#2B2B2B]/60 mt-2 text-base sm:text-lg font-body">
            <Eye className="w-4 h-4 inline mr-1" /> Guardian view — here's how your student is progressing.
          </p>
        </div>

        {students.length === 0 ? (
          <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-8 sm:p-10 text-center">
            <Eye className="w-10 h-10 text-[#EADBC8] mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#5C3A2E] font-semibold mb-2">No students linked yet</h2>
            <p className="text-[#2B2B2B]/50 text-sm max-w-md mx-auto">
              Your studio manager will link you to your student shortly.
            </p>
          </div>
        ) : (
          students.map((entry) => (
            <StudentSection key={entry.student.id} entry={entry} />
          ))
        )}
      </div>
    </StudentLayout>
  );
}

function StudentSection({ entry }) {
  const { student, course, group, current_module, modules, completed_ids, sessions, progress } = entry;

  return (
    <div className="space-y-6">
      {/* Student Header */}
      <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-[#EADBC8] flex items-center justify-center overflow-hidden">
            {student.avatar_url ? (
              <img src={`${API_BASE}/api/files/${student.avatar_url}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-lg font-semibold text-[#5C3A2E]">{student.name?.[0]?.toUpperCase() || "?"}</span>
            )}
          </div>
          <div>
            <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold">{student.name}</h2>
            {course && <p className="text-sm text-[#2B2B2B]/50">{course.title}</p>}
            {group && <p className="text-xs text-[#C6A969]">{group.name} · {group.schedule}</p>}
          </div>
        </div>

        {/* Progress Bar */}
        {progress.total > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40">Progress</span>
              <span className="text-sm font-semibold text-[#5C3A2E]">{progress.completed}/{progress.total} modules</span>
            </div>
            <div className="w-full h-3 bg-[#EADBC8]/30 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#5C3A2E] to-[#C6A969] rounded-full transition-all duration-700" style={{ width: `${progress.percent}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-[#2B2B2B]/40">{progress.percent}% complete</span>
              {current_module && <span className="text-xs text-[#C6A969]">Current: {current_module.title}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Modules List */}
      {modules.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-3">Modules</p>
          <div className="grid gap-2">
            {modules.map((m, i) => {
              const isCompleted = completed_ids.includes(m.id);
              const isCurrent = current_module?.id === m.id;
              return (
                <div key={m.id} className={`rounded-xl border p-4 transition-all ${isCompleted || isCurrent ? "bg-white shadow-[0_4px_15px_rgb(92,58,46,0.06)] border-[#EADBC8]/30" : "bg-[#F7F3EF] border-[#EADBC8]/20 opacity-50"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${isCompleted ? "bg-[#5C3A2E] text-white" : isCurrent ? "bg-[#C6A969] text-white" : "bg-[#EADBC8] text-[#5C3A2E]/50"}`}>
                      {isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-[#2B2B2B] truncate">{m.title}</h4>
                      {isCurrent && <span className="text-xs text-[#C6A969]">Current</span>}
                      {isCompleted && <span className="text-xs text-[#5C3A2E]/50">Completed</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sessions */}
      {sessions.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#2B2B2B]/40 mb-3">Practical Sessions</p>
          <div className="grid gap-3">
            {sessions.map((s) => (
              <div key={s.id} className="bg-white rounded-xl shadow-[0_4px_15px_rgb(92,58,46,0.06)] border border-[#EADBC8]/30 p-4 space-y-3">
                <div>
                  <h4 className="font-heading text-base text-[#5C3A2E] font-semibold">{s.title}</h4>
                  {s.description && <p className="text-[#2B2B2B]/50 text-sm mt-0.5">{s.description}</p>}
                </div>
                {s.notes && (
                  <div className="text-[#2B2B2B]/70 text-sm leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.notes) }} />
                )}
                {s.materials && s.materials.length > 0 && (
                  <div className="space-y-1">
                    {s.materials.map((mat, i) => (
                      <a key={mat.id || i} href={mat.type === "link" ? mat.url : `${API_BASE}/api/files/${mat.storage_path}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-lg bg-[#F7F3EF] hover:bg-[#EADBC8]/40 transition-colors text-sm text-[#2B2B2B]">
                        {mat.type === "link" ? <ExternalLink className="w-3 h-3 text-[#C6A969]" /> : <Download className="w-3 h-3 text-[#5C3A2E]" />}
                        {mat.name || "Resource"}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
